/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import Denque from 'denque';
import { assert, TypedEventEmitter } from '@fluidframework/common-utils';
import { fail, noop } from './Common';
import { RevisionValueCache } from './RevisionValueCache';
import { EditStatus } from './persisted-types';
import { TransactionInternal } from './TransactionInternal';
/**
 * Events reported by {@link CachingLogViewer} for diagnostics or testing purposes.
 */
export var CachingLogViewerDiagnosticEvents;
(function (CachingLogViewerDiagnosticEvents) {
    CachingLogViewerDiagnosticEvents["RevisionRetained"] = "revisionRetained";
})(CachingLogViewerDiagnosticEvents || (CachingLogViewerDiagnosticEvents = {}));
/**
 * Creates views for revisions associated with an EditLog and caches the results.
 *
 * Does so by listening for edits added to the log. If the underlying EditLog or its listeners need to be reused beyond the lifetime of
 * a CachingLogViewer instance, that instance should be disposed with `detachFromEditLog` to ensure it is garbage-collectable.
 * @internal
 */
export class CachingLogViewer extends TypedEventEmitter {
    /**
     * Create a new LogViewer
     * @param log - the edit log which revisions will be based on.
     * @param baseTree - the tree used in the view corresponding to the 0th revision.
     * @param initialRevision - a [sequencedRevision, view] pair that is known (been precomputed) at construction time.
     * This revision is guaranteed to never be evicted from the cache unless it is replaced as the oldest in memory revision.
     * @param expensiveValidation - Iff true, additional correctness assertions will be run during LogViewer operations.
     * @param processEditStatus - called after applying an edit.
     * @param processSequencedEditResult - called after applying a sequenced edit.
     */
    constructor(log, baseView, initialRevision, processEditStatus = noop, processSequencedEditResult = noop, minimumSequenceNumber = 0) {
        super();
        /**
         * A cache for local revisions.
         * It is invalidated whenever a new sequenced edit (that was not already a local edit) is added to the log.
         * When a previously local edit is sequenced, this cache is adjusted to account for it, not invalidated.
         */
        this.localRevisionCache = new Denque();
        /**
         * The ordered queue of edits that originated from this client that have never been applied (by this log viewer) in a sequenced state.
         * This means these edits may be local or sequenced, and may have been applied (possibly multiple times) while still local.
         * Used to log telemetry about the result of edit application. Edits are removed when first applied after being sequenced.
         */
        this.unappliedSelfEdits = new Denque();
        this.log = log;
        if (initialRevision !== undefined) {
            assert(Number.isInteger(initialRevision[0]), 0x628 /* revision must be an integer */);
            assert(this.log.isSequencedRevision(initialRevision[0]), 0x629 /* revision must correspond to the result of a SequencedEdit */);
        }
        this.sequencedRevisionCache = new RevisionValueCache(CachingLogViewer.sequencedCacheSizeMax, minimumSequenceNumber, initialRevision !== null && initialRevision !== void 0 ? initialRevision : [0, { view: baseView }]);
        this.processEditStatus = processEditStatus !== null && processEditStatus !== void 0 ? processEditStatus : noop;
        this.processSequencedEditResult = processSequencedEditResult !== null && processSequencedEditResult !== void 0 ? processSequencedEditResult : noop;
        this.detachFromEditLog = this.log.registerEditAddedHandler(this.handleEditAdded.bind(this));
        // Registers a handler that is called when edits are evicted
        this.log.registerEditEvictionHandler(this.evictCachedRevisions.bind(this));
    }
    /**
     * @returns true if the highest revision is cached.
     */
    highestRevisionCached() {
        return this.highestRevisionCacheEntry !== undefined;
    }
    /**
     * As a performance optimization, this method caches views generated by local edits if they are sequenced without
     * being interleaved with remote edits.
     */
    handleEditAdded(edit, isLocal, wasLocal) {
        // Clear highestRevisionCacheEntry, since what revision is highest might change.
        // Note that as an optimization we could skip clearing this when a local edit is sequenced.
        this.highestRevisionCacheEntry = undefined;
        if (isLocal) {
            this.unappliedSelfEdits.push(edit.id);
        }
        else if (wasLocal) {
            // If the new sequenced edit was generated by this client, the corresponding cache entry (if there is one)
            // will be at the front of the queue. If the queue is empty, then a concurrent sequenced edit from remote client
            // must have invalidated the queue cache.
            const entry = this.localRevisionCache.shift();
            if (entry !== undefined) {
                const revision = this.log.numberOfSequencedEdits;
                const { view } = entry;
                this.sequencedRevisionCache.cacheValue(revision, entry.status === EditStatus.Applied
                    ? {
                        view,
                        status: entry.status,
                        steps: entry.steps,
                    }
                    : {
                        view,
                        status: entry.status,
                        failure: entry.failure,
                    });
                this.handleSequencedEditResult(edit, entry, []);
            }
        }
        else {
            // Invalidate any cached results of applying edits which are ordered after `edit` (which are all remaining local edits)
            this.localRevisionCache.clear();
        }
    }
    /**
     * {@inheritDoc LogViewer.getRevisionViewInMemory}
     */
    getRevisionViewInMemory(revision) {
        return this.getEditResultInMemory(revision).view;
    }
    /**
     * @returns the {@link EditCacheEntry} for the requested revision
     */
    getEditResultInMemory(revision) {
        var _a;
        assert(revision >= this.log.earliestAvailableEditIndex, 0x62a /* revision not stored in memory */);
        const startingPoint = this.getStartingPoint(revision);
        const { startRevision } = startingPoint;
        let current = startingPoint;
        for (let i = startRevision; i < revision && i < this.log.length; i++) {
            const edit = (_a = this.log.tryGetEditAtIndex(i)) !== null && _a !== void 0 ? _a : fail('edit not found');
            current = this.applyEdit(current.view, edit, i);
        }
        return current;
    }
    /**
     * Informs the CachingLogViewer of the latest known minimumSequenceNumber for all connected clients.
     * This can be used to provide more aggressive caching of revisions within the collaboration window, as those revisions
     * are more likely to be demanded to resolve conflicts.
     * @param minSequenceNumber - the minimum known sequence number of all connected clients.
     */
    setMinimumSequenceNumber(minimumSequenceNumber) {
        // Sequence numbers in Fluid are 1-indexed, meaning they correspond to revisions, and can be used as revisions.
        // This ensures that all revisions >= minimumSequenceNumber are kept in the cache, meaning that even if all clients are caught up
        // the most recent sequenced revision will be cached.
        this.sequencedRevisionCache.updateRetentionWindow(minimumSequenceNumber);
    }
    /**
     * Inform the CachingLogViewer that a particular edit is known to have a specific result when applied to a particular TreeView.
     * CachingLogViewer may use this information as an optimization to avoid re-running the edit if re-applied to the same TreeView.
     */
    setKnownEditingResult(edit, result) {
        this.cachedEditResult = { editId: edit.id, result };
    }
    /**
     * Handler that is called before the stored edit log evicts any edits.
     * This caches the revision that corresponds to the edit that will be the oldest in memory after eviction
     * to ensure that there is always a base revision that any in memory edit can be applied to.
     */
    evictCachedRevisions(editsToEvict) {
        const revisionToRetain = this.log.earliestAvailableEditIndex + editsToEvict;
        const cacheEntry = this.getEditResultInMemory(revisionToRetain);
        this.sequencedRevisionCache.cacheRetainedValue(revisionToRetain, cacheEntry);
        this.emit(CachingLogViewerDiagnosticEvents.RevisionRetained, revisionToRetain);
    }
    /**
     * @returns the cached revision view closest to the requested `revision`.
     */
    getStartingPoint(revision) {
        var _a, _b, _c;
        // Per the documentation for revision, the returned view should be the output of the edit at the largest index <= `revision`.
        const revisionClamped = Math.min(revision, this.log.length);
        // If the highest revision is requested, and it's cached, use highestRevisionCacheEntry.
        if (revisionClamped === this.log.length && this.highestRevisionCacheEntry !== undefined) {
            return Object.assign(Object.assign({}, this.highestRevisionCacheEntry), { startRevision: revisionClamped });
        }
        let current;
        let startRevision;
        const { numberOfSequencedEdits } = this.log;
        const isLocalRevision = revisionClamped > numberOfSequencedEdits;
        if (isLocalRevision && !this.localRevisionCache.isEmpty()) {
            const { length } = this.localRevisionCache;
            // Local revision view cache is indexed such that the view for revision 0 (a local edit) is stored at index 0 in the cache.
            // This is because the local cache does not contain an entry for the implicit initial tree edit.
            const localCacheIndex = revisionClamped - 1 - numberOfSequencedEdits;
            if (localCacheIndex < length) {
                const cached = (_a = this.localRevisionCache.peekAt(localCacheIndex)) !== null && _a !== void 0 ? _a : fail('missing tail of localRevisionViewCache');
                return Object.assign(Object.assign({}, cached), { startRevision: revisionClamped });
            }
            else {
                current = (_b = this.localRevisionCache.peekAt(length - 1)) !== null && _b !== void 0 ? _b : fail('missing tail of localRevisionViewCache');
                startRevision = numberOfSequencedEdits + length;
            }
        }
        else {
            const [cachedRevision, cachedView] = (_c = this.sequencedRevisionCache.getClosestEntry(revisionClamped)) !== null && _c !== void 0 ? _c : fail('No preceding revision view cached.');
            startRevision = cachedRevision;
            current = cachedView;
        }
        return Object.assign({ startRevision }, current);
    }
    /**
     * Helper for applying an edit at the supplied revision view.
     * Must only be called in the order that edits appear in the log.
     * Must only be called once for a given local edit as long as the local cache has not been invalidated.
     * Must only be called once for a given sequenced edit.
     * @returns the resulting revision view and the outcome of edit that produced it.
     */
    applyEdit(prevView, edit, editIndex) {
        let editingResult;
        let cached;
        let reconciliationPath = [];
        if (this.cachedEditResult !== undefined &&
            this.cachedEditResult.editId === edit.id &&
            this.cachedEditResult.result.before === prevView) {
            editingResult = this.cachedEditResult.result;
            cached = true;
        }
        else {
            reconciliationPath = this.reconciliationPathFromEdit(edit.id);
            editingResult = TransactionInternal.factory(prevView)
                .applyChanges(edit.changes, reconciliationPath)
                .close();
            cached = false;
        }
        const revision = editIndex + 1;
        const nextView = editingResult.status === EditStatus.Applied ? editingResult.after : prevView;
        const computedCacheEntry = editingResult.status === EditStatus.Applied
            ? { view: nextView, status: editingResult.status, steps: editingResult.steps }
            : { view: nextView, status: editingResult.status, failure: editingResult.failure };
        if (this.log.isSequencedRevision(revision)) {
            this.sequencedRevisionCache.cacheValue(revision, computedCacheEntry);
            this.handleSequencedEditResult(edit, computedCacheEntry, reconciliationPath);
        }
        else {
            // This relies on local edits being append only, and that generating the view for a local revision requires generating
            // the views for all local revisions before it in the log. Thus, generating such a view will necessarily require
            // calls to this method for all local revisions prior, guaranteeing the correct push order.
            assert(revision === this.log.numberOfSequencedEdits + this.localRevisionCache.length + 1, 0x62b /* Local revision view cached out of order. */);
            this.localRevisionCache.push(computedCacheEntry);
        }
        // Only update highestRevisionCacheEntry if this snapshot is the highest revision.
        if (revision >= this.log.length) {
            this.highestRevisionCacheEntry = computedCacheEntry;
        }
        this.processEditStatus(editingResult.status, this.log.getIdAtIndex(editIndex), cached);
        return computedCacheEntry;
    }
    /**
     * Helper for performing caching when a sequenced local edit is first applied.
     * Invokes the `processSequencedEditResult` handler that was passed to the constructor (if any).
     * Must only be called for non-cached sequenced edits.
     */
    handleSequencedEditResult(edit, result, reconciliationPath) {
        let wasLocal = false;
        // This is the first time this sequenced edit has been processed by this LogViewer. If it was a local edit, log telemetry
        // in the event that it was invalid or malformed.
        if (this.unappliedSelfEdits.length > 0) {
            if (edit.id === this.unappliedSelfEdits.peekFront()) {
                wasLocal = true;
                this.unappliedSelfEdits.shift();
            }
        }
        this.processSequencedEditResult({ edit, wasLocal, result, reconciliationPath });
    }
    /**
     * We currently compute only the "main branch" part of the reconciliation path (meaning we don't include inverts of the edits
     * that occurred on the rebased branch). Doing so is only needed for the sequential anchor resolution approach which is not
     * yet supported.
     * @param editId - The ID for the edit to get the reconciliation path for.
     */
    reconciliationPathFromEdit(editId) {
        const reconciliationPath = [];
        let cached = false;
        return new Proxy(reconciliationPath, {
            get: (target, prop) => {
                if (!cached) {
                    cached = true;
                    const orderedId = this.log.getOrderedEditId(editId);
                    if (orderedId.isLocal === false && orderedId.sequenceInfo !== undefined) {
                        const earliestSequenced = this.earliestSequencedEditInMemory();
                        if (earliestSequenced !== undefined) {
                            const earliestEditSequenceNumber = earliestSequenced.sequenceNumber;
                            const targetSequenceNumber = Math.max(earliestEditSequenceNumber, orderedId.sequenceInfo.referenceSequenceNumber);
                            if (targetSequenceNumber < orderedId.sequenceInfo.sequenceNumber) {
                                const firstEdit = this.getEditResultFromSequenceNumber(targetSequenceNumber);
                                if (firstEdit !== undefined) {
                                    if (firstEdit.status === EditStatus.Applied) {
                                        const firstEditInfo = this.log.getOrderedEditId(firstEdit.id);
                                        if (firstEditInfo.sequenceInfo !== undefined &&
                                            firstEditInfo.sequenceInfo.sequenceNumber >
                                                orderedId.sequenceInfo.referenceSequenceNumber) {
                                            reconciliationPath.push(Object.assign(Object.assign({}, firstEdit.steps), { before: firstEdit.before, after: firstEdit.view, length: firstEdit.steps.length }));
                                        }
                                    }
                                    const lowestIndex = this.log.getIndexOfId(firstEdit.id) + 1;
                                    const highestIndex = this.log.getIndexOfId(editId) - 1;
                                    for (let index = lowestIndex; index <= highestIndex; ++index) {
                                        const edit = this.getEditResultFromIndex(index);
                                        if (edit.status === EditStatus.Applied) {
                                            reconciliationPath.push(Object.assign(Object.assign({}, edit.steps), { before: edit.before, after: edit.view, length: edit.steps.length }));
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                return target[prop];
            },
        });
    }
    /**
     * @returns Edit information for the earliest known sequenced edit.
     */
    earliestSequencedEditInMemory() {
        const earliestEditIndex = this.log.earliestAvailableEditIndex;
        const lastSequencedEdit = this.log.numberOfSequencedEdits + earliestEditIndex - 1;
        for (let index = earliestEditIndex; index <= lastSequencedEdit; ++index) {
            const edit = this.log.tryGetEditAtIndex(index);
            if (edit !== undefined) {
                const editOrderedId = this.log.getOrderedEditId(edit.id);
                if (editOrderedId.sequenceInfo !== undefined) {
                    return { edit, sequenceNumber: editOrderedId.sequenceInfo.sequenceNumber };
                }
            }
        }
        return undefined;
    }
    /**
     * @returns Edit result information for the edit at the given `index`.
     */
    getEditResultFromIndex(index) {
        var _a;
        const edit = (_a = this.log.tryGetEditAtIndex(index)) !== null && _a !== void 0 ? _a : fail('edit does not exist in memory');
        const before = this.getRevisionViewInMemory(index);
        const resultAfter = this.getEditResultInMemory(index + 1);
        if (resultAfter.status === undefined) {
            fail('The status of every edit in memory should be known');
        }
        return resultAfter.status === EditStatus.Applied
            ? {
                id: edit.id,
                status: EditStatus.Applied,
                before,
                changes: edit.changes,
                view: resultAfter.view,
                steps: resultAfter.steps,
            }
            : {
                id: edit.id,
                status: resultAfter.status,
                failure: resultAfter.failure,
                before,
                view: resultAfter.view,
                changes: edit.changes,
            };
    }
    /**
     * @param sequenceNumber - The server-assigned sequenced number assigned to the edit of interest.
     * @returns Edit result information for the edit with the given sequence number or the nearest sequenced edit before that.
     * Undefined if no sequenced edit occurred at or prior to the given sequenceNumber.
     */
    getEditResultFromSequenceNumber(sequenceNumber) {
        const earliestSequenced = this.earliestSequencedEditInMemory();
        if (earliestSequenced !== undefined && sequenceNumber >= earliestSequenced.sequenceNumber) {
            const lowestIndex = this.log.getIndexOfId(earliestSequenced.edit.id);
            const highestIndex = this.log.numberOfSequencedEdits - 1;
            for (let index = highestIndex; index >= lowestIndex; --index) {
                const edit = this.log.tryGetEditAtIndex(index);
                if (edit !== undefined) {
                    const orderedId = this.log.getOrderedEditId(edit.id);
                    // If `orderedId.sequenceInfo.sequenceNumber` is equal to the requested `sequenceNumber` then we have found the edit of
                    // interest and simply return its associated information.
                    // Note that the check bellow also is also satisfied if `orderedId.sequenceInfo.sequenceNumber`is lower than the requested
                    // `sequenceNumber`. This can happen when the edit for the requested `sequenceNumber` has either not yet been received or
                    // has been processed by a different DDS (several DDSes can share the same stream of operations and will only see those
                    // relevant to them). In such cases, we return the edit info for the last known edit before that.
                    if (orderedId.sequenceInfo && orderedId.sequenceInfo.sequenceNumber <= sequenceNumber) {
                        const before = this.getRevisionViewInMemory(index);
                        const resultAfter = this.getEditResultInMemory(index + 1);
                        if (resultAfter.status === undefined) {
                            fail('The status of every edit in session should be known');
                        }
                        return resultAfter.status === EditStatus.Applied
                            ? {
                                id: edit.id,
                                status: EditStatus.Applied,
                                before,
                                changes: edit.changes,
                                view: resultAfter.view,
                                steps: resultAfter.steps,
                            }
                            : {
                                id: edit.id,
                                status: resultAfter.status,
                                failure: resultAfter.failure,
                                before,
                                view: resultAfter.view,
                                changes: edit.changes,
                            };
                    }
                }
            }
        }
        return undefined;
    }
    // DEPRECATED APIS
    /**
     * @deprecated Edit virtualization is no longer supported, do not use the asynchronous APIs.
     */
    async getEditResult(revision) {
        return this.getEditResultInMemory(revision);
    }
    /**
     * @deprecated Edit virtualization is no longer supported, use {@link LogViewer.getRevisionViewInMemory}
     */
    async getRevisionView(revision) {
        return this.getEditResultInMemory(revision).view;
    }
    /**
     * @deprecated Edit virtualization is no longer supported, do not use the 'InSession' APIs.
     */
    getEditResultInSession(revision) {
        return this.getEditResultInMemory(revision);
    }
    /**
     * @deprecated Edit virtualization is no longer supported, use {@link LogViewer.getRevisionViewInMemory}
     */
    getRevisionViewInSession(revision) {
        return this.getEditResultInMemory(revision).view;
    }
}
/**
 * Maximum size of the sequenced revision cache.
 */
CachingLogViewer.sequencedCacheSizeMax = 50;
//# sourceMappingURL=LogViewer.js.map